import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { X } from 'lucide-react';
import React, { useEffect } from 'react';
import useIsDev from 'shared/hooks/use-is-dev';
import UDButton from 'ud-ui/components/button';
import UDIconButton from 'ud-ui/components/icon-button';
import { SKELETON_OVERLAY_CLASS, SKELETON_PREVIEW_HOTKEY } from 'ud-ui/components/skeleton/skeleton-tokens';
import UDText from 'ud-ui/components/text';
import {
  SKELETON_PREVIEW_MODES,
  SkeletonPreviewMode,
  setSkeletonPreviewMode,
  setSkeletonPreviewPanelOpen,
  useRegisterSkeletonPreview,
  useSkeletonPreviewMode,
  useSkeletonPreviewMountedCount,
  useSkeletonPreviewPanelOpen,
} from './skeleton-preview-store';

interface SkeletonPreviewProps {
  /** Скелетон того же состояния экрана — тот, что рисуется на время загрузки. */
  skeleton: React.ReactNode;
  /** Загруженный экран. */
  children: React.ReactNode;
}

/**
 * Обёртка загруженного экрана для сверки со скелетоном при локальной разработке (`npm start`).
 *
 * Без неё скелетон виден только в окно загрузки, и сравнить его с экраном можно лишь задержкой
 * ответа и замером. Режим выбирается панелью `SkeletonPreviewSwitch` (она в шелле). В сборке для
 * стендов и прода обёртка прозрачна: `useIsDev` там всегда `false`.
 */
export const SkeletonPreview = ({ skeleton, children }: SkeletonPreviewProps) => {
  const isDev = useIsDev();
  const mode = useSkeletonPreviewMode();
  useRegisterSkeletonPreview(isDev);

  if (!isDev || mode === 'off') return <>{children}</>;
  if (mode === 'skeleton') return <>{skeleton}</>;

  return (
    <div className="relative w-full" data-testid="skeleton-preview-overlay">
      {children}
      {/* Экран под наложением остаётся кликабельным: «Сменить» открывает второй вариант скелетона. */}
      <div className={classNames('pointer-events-none absolute inset-x-0 top-0', SKELETON_OVERLAY_CLASS)} aria-hidden>
        {skeleton}
      </div>
    </div>
  );
};

const MODE_LABELS: Record<SkeletonPreviewMode, MessageDescriptor> = {
  off: msg`Экран`,
  skeleton: msg`Скелетон`,
  overlay: msg`Наложение`,
};

const isHotkey = (event: KeyboardEvent) =>
  event.code === SKELETON_PREVIEW_HOTKEY.code &&
  event.ctrlKey === SKELETON_PREVIEW_HOTKEY.ctrlKey &&
  event.shiftKey === SKELETON_PREVIEW_HOTKEY.shiftKey &&
  event.altKey === SKELETON_PREVIEW_HOTKEY.altKey &&
  !event.metaKey;

/**
 * Панель «Сверка скелетона». Монтируется один раз в шелле и рисуется, только когда на экране есть
 * хотя бы одна `SkeletonPreview`. Крестик и хоткей (`SKELETON_PREVIEW_HOTKEY`) прячут и
 * возвращают её; режим при этом не сбрасывается — без панели удобно смотреть чистое наложение.
 * Слой `toast` — выше модалок: окна со скелетоном тоже сверяются.
 */
export const SkeletonPreviewSwitch = () => {
  const { t } = useLingui();
  const isDev = useIsDev();
  const mode = useSkeletonPreviewMode();
  const isPanelOpen = useSkeletonPreviewPanelOpen();
  const mountedCount = useSkeletonPreviewMountedCount();
  const isAvailable = isDev && mountedCount > 0;

  useEffect(() => {
    if (!isAvailable) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isHotkey(event)) return;
      event.preventDefault();
      setSkeletonPreviewPanelOpen(!isPanelOpen);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isAvailable, isPanelOpen]);

  if (!isAvailable || !isPanelOpen) return null;

  const hotkey = SKELETON_PREVIEW_HOTKEY.label;

  return (
    <div
      className="bg-surfaceCard rounded-panel shadow-medium z-toast fixed bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 p-2"
      role="group"
      aria-label={t`Сверка скелетона`}
      data-testid="skeleton-preview-switch"
    >
      <UDText type="footnote" className="text-inkSecondary px-2">
        <Trans>Сверка скелетона</Trans>
      </UDText>
      {SKELETON_PREVIEW_MODES.map((option) => (
        <UDButton
          key={option}
          variant={option === mode ? 'primary' : 'secondary'}
          type="button"
          aria-pressed={option === mode}
          onClick={() => setSkeletonPreviewMode(option)}
        >
          {t(MODE_LABELS[option])}
        </UDButton>
      ))}
      <UDText type="footnote" className="text-inkMuted px-2 whitespace-nowrap">
        {hotkey}
      </UDText>
      <UDIconButton
        icon={<X />}
        variant="ghost"
        aria-label={t`Скрыть панель (${hotkey})`}
        tooltip={t`Скрыть панель (${hotkey})`}
        onClick={() => setSkeletonPreviewPanelOpen(false)}
        data-testid="skeleton-preview-close"
      />
    </div>
  );
};
